/**
 * Calendar dates. "Today" is the Manila date; the arithmetic is zone-free.
 *
 * Every one of these existed already, copied into five route files, written as
 * `new Date().toISOString().slice(0, 10)`. That is UTC. Australia is UTC+8 to UTC+11, so from
 * midnight until 10am (AEST) it returns YESTERDAY, and any week derived from a Sunday rolls back
 * to the previous week. A customer reported both on 2026-08-24: "this morning it was still
 * showing Sunday, and on Week it was showing last week."
 *
 * The same mistake made date arithmetic silently wrong at every hour: `addDays` built LOCAL
 * midnight and then read it back as UTC, losing the offset, so east of UTC `addDays(iso, 1)`
 * returned `iso` and the day arrows did nothing.
 *
 * A calendar date is not an instant. Format and step it in local parts, never through UTC.
 */

import { manilaDayIso } from './ph'

/** A Date as `YYYY-MM-DD`, using its local calendar parts. */
export const dayIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/**
 * Today, in MANILA (spec 001). Not the device's zone: a trade person whose laptop is still on
 * Sydney time after a trip must not see tomorrow's jobs as today's. `dayIso` and the arithmetic
 * below stay in local parts, which is correct for them: a calendar date built by `isoToDate` and
 * read back by `dayIso` round-trips in any zone.
 */
export const todayIso = () => manilaDayIso(new Date())

/** Local midnight for a `YYYY-MM-DD`. The `T00:00:00` form parses as local time; a bare date does not. */
export const isoToDate = (iso: string) => new Date(`${iso}T00:00:00`)

export const addDaysIso = (iso: string, n: number) => {
  const d = isoToDate(iso)
  d.setDate(d.getDate() + n)
  return dayIso(d)
}

export const addMonthsIso = (iso: string, n: number) => {
  const d = isoToDate(iso)
  d.setMonth(d.getMonth() + n)
  return dayIso(d)
}

/** Monday of the week containing `iso`. */
export const startOfWeekIso = (iso: string) => {
  const d = isoToDate(iso)
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return dayIso(d)
}

/**
 * Decide where a date-driven screen should sit after the clock has moved.
 *
 * A screen seeded with `useState(todayIso())` never moves again, so a tab left open overnight
 * still shows yesterday, and any range derived from it is a week behind.
 *
 * `auto` is the date the screen put there itself. While `current` still equals it the user has
 * not navigated, so it is safe to roll forward. Once they pick another day the two differ and
 * their choice is left alone: looking at next week must not snap back to today. `auto` still
 * advances, so pressing Today re-arms the follow.
 */
export function nextAutoDate(current: string, auto: string, today: string): { date: string; auto: string } {
  if (today === auto) return { date: current, auto }
  return { date: current === auto ? today : current, auto: today }
}
